import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

export interface Business {
  id: string;
  name: string;
  abn: string;
  deviceCount: number;
  contactCount: number;
  address?: {
    street: string;
    city: string;
    state: string;
    postCode: string;
  };
}

interface BusinessContextType {
  businesses: Business[];
  selectedBusinessId: string | null;
  selectedBusiness: Business | null;
  isLoading: boolean;
  setSelectedBusinessId: (businessId: string) => void;
  refreshBusinesses: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

const STORAGE_KEY = 'dmo_selected_business_id';

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setSelectedBusinessId = useCallback((businessId: string) => {
    localStorage.setItem(STORAGE_KEY, businessId);
    setSelectedBusinessIdState(businessId);
  }, []);

  const loadBusinesses = useCallback(async () => {
    if (!auth.isSignedIn) {
      setBusinesses([]);
      setSelectedBusinessIdState(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/businesses', {
        headers: { 'Authorization': `Bearer ${auth.tokens?.accessToken}` },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }
      const data: Business[] = await response.json();
      setBusinesses(data);

      if (data.length === 0) {
        setSelectedBusinessIdState(null);
        return;
      }

      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId && data.some((b) => b.id === storedId)) {
        setSelectedBusinessIdState(storedId);
        return;
      }

      // Fall back to the user's default business, then the first business
      let defaultBusinessId: string | null = null;
      try {
        const meResponse = await fetch('/api/users/me', {
          headers: { 'Authorization': `Bearer ${auth.tokens?.accessToken}` },
        });
        if (meResponse.ok) {
          const me = await meResponse.json();
          defaultBusinessId = me.defaultBusinessId ?? null;
        }
      } catch {
        // Fall through to first-business default below
      }

      const fallbackId = (defaultBusinessId && data.some((b) => b.id === defaultBusinessId))
        ? defaultBusinessId
        : data[0].id;

      localStorage.setItem(STORAGE_KEY, fallbackId);
      setSelectedBusinessIdState(fallbackId);
    } catch (err) {
      console.error('Failed to load businesses:', err);
      setBusinesses([]);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId) ?? null;

  const value: BusinessContextType = {
    businesses,
    selectedBusinessId,
    selectedBusiness,
    isLoading,
    setSelectedBusinessId,
    refreshBusinesses: loadBusinesses,
  };

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

export const useBusiness = (): BusinessContextType => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};
